/* eslint-disable no-unused-vars */
import 'constants/constants.json';
import { ScriptLoaderService } from './scriptLoader.service';
import { ApiService } from './api.service';
import { CheckoutService } from './checkout.service';
import { createCartAddress, formatZipCode } from './cartUtils';

export const PaypalSdkService = angular
    .module('checkout.services.paypalSdkService', [
        ScriptLoaderService,
        ApiService,
        CheckoutService
    ]).service('paypalSdkService', (
        scriptLoaderService,
        $window,
        apiService,
        BN_CODE,
        PAYPAL_CLIENT_ID,
        DEFAULT_ERROR_MSG,
        checkoutService,
        $q,
        $rootScope,
        $timeout
    ) => {
        let merchantId;
        let intent;
        let useNewCarts = false;
        let paypalInfo;
        let total;
        let hostedFields;
        function init({
            storeInfo,
            cartId,
            bagId,
            grandTotal,
            newCarts = false,
            getGiftCards
        }) {
            merchantId = isValidMerchantId(storeInfo) ? storeInfo.paypal.merchantPayerId : undefined;
            intent = getPaymentAction(storeInfo);
            paypalInfo = storeInfo.paypal;
            total = grandTotal;
            useNewCarts = newCarts;
            loadCheckoutScript(cartId, bagId, getGiftCards);
        }
        function loadCheckoutScript(cartId, bagId, getGiftCards) {
            if (!$window.paypal) {
                let identityParams = `client-id=${PAYPAL_CLIENT_ID}`;
                if (merchantId) {
                    identityParams += `&merchant-id=${merchantId}`;
                }
                if (paypalInfo.acpStatus === 'SUBSCRIBED' && !paypalInfo.acpLimitReached) {
                    const scriptUrl = 'https://www.paypal.com/sdk/js?components=hosted-fields,buttons,messages';
                    const transactionParams = `currency=USD&intent=${intent}&commit=false&enable-funding=venmo`;
                    const paypalJsUrl = `${scriptUrl}&${identityParams}&${transactionParams}`;
                    apiService.getPayPalClientToken().then(({ client_token: clientToken }) => {
                        scriptLoaderService.loadScriptTag({
                            src: paypalJsUrl,
                            async: true,
                            attributes: [
                                { name: 'data-client-token', value: clientToken },
                                { name: 'data-partner-attribution-id', value: BN_CODE }
                            ],
                            onload: () => {
                                // Load acp fields and buttons
                                renderPaypalAcp(cartId, bagId);
                                renderAllPaypalButtons(cartId, bagId, getGiftCards);
                                renderPaypalMessages();
                            }
                        });
                    }).catch((err) => {
                        loadButtonsScript(cartId, bagId, identityParams, getGiftCards);
                    });
                } else {
                    loadButtonsScript(cartId, bagId, identityParams, getGiftCards);
                }
            }
        }
        function loadButtonsScript(cartId, bagId, identityParams, getGiftCards) {
            const scriptUrl = 'https://www.paypal.com/sdk/js?components=messages,buttons';
            const transactionParams = `intent=${intent}&commit=false&enable-funding=venmo`;
            const paypalJsUrl = `${scriptUrl}&${identityParams}&${transactionParams}`;
            scriptLoaderService.loadScriptTag({
                src: paypalJsUrl,
                async: true,
                onload: () => {
                    renderAllPaypalButtons(cartId, bagId, getGiftCards);
                    renderPaypalMessages();
                }
            });
        }
        function renderAllPaypalButtons(cartId, bagId, getGiftCards) {
            renderPaypalButtons(cartId, bagId, getGiftCards);
            renderPaypalSummaryButtons(cartId, bagId, getGiftCards);
        }
        function renderPaypalMessages() {
            if (!paypalInfo.payInFourEnabled || !$window.paypal) {
                return;
            }

            const messagesSelector = '.paypal-pay-in-four-messaging';
            const messagesContainer = $window.document.querySelector(messagesSelector);

            if (messagesContainer && messagesContainer.innerHTML.length > 0 && $window.paypal.messagesRendered) {
                // Messages already rendered, move them to PayPal label
                $timeout(() => {
                    const inlinePaymentSelector = '.payment-method-form__pay-in-four';
                    const inlinePaymentContainer = $window.document.querySelector(inlinePaymentSelector);
                    if (inlinePaymentContainer) {
                        inlinePaymentContainer.appendChild(messagesContainer);
                        messagesContainer.style.display = 'block';
                    }
                });
            } else if (messagesContainer && !$window.paypal.messagesRendered) {
                $timeout(() => {
                    // https://developer.paypal.com/docs/business/pay-later/advanced/reference/#messages-function
                    // eslint-disable-next-line new-cap
                    $window.paypal.Messages({
                        amount: total || '',
                        style: {
                            layout: 'text',
                            logo: {
                                type: 'inline'
                            }
                        },
                        placement: 'payment'
                    }).render(messagesContainer)
                        .then(() => {
                            // TODO Remove below once PayPal issue is addressed
                            // https://github.com/paypal/paypal-messaging-components/issues/463
                            const inlinePaymentSelector = '.payment-method-form__pay-in-four';
                            const inlinePaymentContainer = $window.document.querySelector(inlinePaymentSelector);
                            $window.paypal.messagesRendered = true;
                            if (inlinePaymentContainer) {
                                inlinePaymentContainer.appendChild(messagesContainer);
                            } else {
                                messagesContainer.style.display = 'none';
                            }
                        });
                });
            }
        }
        function renderPaypalAcp(cartId, bagId) {
            const container = '#paypal-cc-form';
            // eslint-disable-next-line max-len
            if ($window.paypal && $window.document.querySelector(container) && $window.paypal.HostedFields.isEligible()) {
                $window.paypal.HostedFields.render({
                    createOrder: (data, actions) => {
                        return useNewCarts ? startCheckoutFlow(cartId) : legacyStartCheckoutFlow(cartId);
                    },
                    styles: {
                        input: {
                            'font-size': '14px',
                            // eslint-disable-next-line max-len
                            'font-family': '-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,Oxygen-Sans,Ubuntu,Cantarell,"Helvetica Neue",sans-serif',
                            color: '#333',
                            padding: '12px'
                        },
                        ':focus': {
                            color: 'black'
                        }
                    },
                    fields: {
                        number: {
                            selector: '#cardNumber',
                            placeholder: 'Credit Card Number'
                        },
                        cvv: {
                            selector: '#cvc',
                            placeholder: 'CVV'
                        },
                        expirationDate: {
                            selector: '#expiration',
                            placeholder: 'MM/YYYY'
                        }
                    }
                }).then((cardFields) => {
                    cardFields.on('cardTypeChange', (event) => {
                        const cardType = event.cards.length === 1 ? event.cards[0].niceType : 'None';
                        $rootScope.$emit('paypal-card-type-change', { cardType });
                    });
                    hostedFields = cardFields;
                }).catch((err) => {
                    checkoutService.showErrorDialog(err.message);
                });
            }
        }
        function renderPaypalButtons(cartId, bagId, getGiftCards) {
            const container = '#paypal-sdk-button-container';
            if ($window.paypal && $window.document.querySelector(container)) {
                doRender({ cartId, bagId, container, getGiftCards });
            }
        }
        function renderPaypalSummaryButtons(cartId, bagId, getGiftCards) {
            const container = '#paypal-sdk-summary-button-container';
            const style = { layout: 'horizontal' };
            if ($window.paypal && $window.document.querySelector(container)) {
                doRender({ cartId, bagId, container, style, getGiftCards });
            }
        }
        function doRender({ cartId, bagId, container, style, getGiftCards }) {
            const giftCards = getGiftCards();
            if ($window.document.querySelector(container).innerHTML === '') {
                const returnUrlBase = `${$window.location.origin}${$window.location.pathname}`;
                // eslint-disable-next-line new-cap
                $window.paypal.Buttons({
                    style,
                    createOrder: (data, actions) => {
                        return useNewCarts
                            ? startCheckoutFlow(cartId, getGiftCards)
                            : legacyStartCheckoutFlow(cartId, getGiftCards);
                    },
                    onApprove: (data, actions) => {
                        // eslint-disable-next-line max-len
                        return actions.redirect(`${returnUrlBase}?token=${data.orderID}&PayerID=${data.payerID}&paymentId=${data.paymentID}&bag=${bagId}`);
                    },
                    onCancel: (data, actions) => {
                        actions.restart();
                    },
                    onError: (err) => {
                        // eslint-disable-next-line max-len
                        checkoutService.showErrorDialog('We were unable to connect to your Paypal account. Please check your Paypal account settings.');
                    }
                }).render(container);
            }
        }
        function isValidMerchantId(storeInfo) {
            return angular.isDefined(storeInfo.paypal)
                && angular.isDefined(storeInfo.paypal.merchantPayerId)
                && storeInfo.paypal.merchantPayerId !== ''
                && (storeInfo.paypal.primaryEmailConfirmed === 'true'
                    || storeInfo.paypal.primaryEmailConfirmed === true);
        }
        function savePaypalData(token, payerId) {
            return apiService.getPaypalExpressCheckout({ token, payerId })
                .then((paypalData) => {
                    return checkoutService.saveContactInfoAndShippingAddress({
                        shippingAddressModel: {
                            firstName: paypalData.FIRSTNAME,
                            lastName: paypalData.LASTNAME,
                            address1: paypalData.PAYMENTREQUEST_0_SHIPTOSTREET,
                            address2: paypalData.PAYMENTREQUEST_0_SHIPTOSTREET2,
                            city: paypalData.PAYMENTREQUEST_0_SHIPTOCITY,
                            state: paypalData.PAYMENTREQUEST_0_SHIPTOSTATE,
                            postalCode: formatZipCode(
                                paypalData.PAYMENTREQUEST_0_SHIPTOZIP,
                                paypalData.PAYMENTREQUEST_0_SHIPTOCOUNTRYCODE
                            ),
                            country: paypalData.PAYMENTREQUEST_0_SHIPTOCOUNTRYCODE,
                            shippingAsBilling: true
                        },
                        contactInfoModel: {
                            phone: paypalData.PHONENUM,
                            email: paypalData.EMAIL
                        }
                    }).then(() => paypalData);
                });
        }
        function setPaypalDataToCart(token, payerId) {
            return apiService.getPaypalExpressCheckout({ token, payerId })
                .then((paypalData) => {
                    return checkoutService.applyContactInfoAndShippingAddressToCart({
                        shippingAddressModel: {
                            firstName: paypalData.FIRSTNAME,
                            lastName: paypalData.LASTNAME,
                            address1: paypalData.PAYMENTREQUEST_0_SHIPTOSTREET,
                            address2: paypalData.PAYMENTREQUEST_0_SHIPTOSTREET2,
                            city: paypalData.PAYMENTREQUEST_0_SHIPTOCITY,
                            state: paypalData.PAYMENTREQUEST_0_SHIPTOSTATE,
                            postalCode: formatZipCode(
                                paypalData.PAYMENTREQUEST_0_SHIPTOZIP,
                                paypalData.PAYMENTREQUEST_0_SHIPTOCOUNTRYCODE
                            ),
                            country: paypalData.PAYMENTREQUEST_0_SHIPTOCOUNTRYCODE,
                            shippingAsBilling: true
                        },
                        contactInfoModel: {
                            phone: paypalData.PHONENUM,
                            email: paypalData.EMAIL
                        }
                    }).then(updatedCart => ({ updatedCart, paypalData }));
                });
        }
        function submitPayPalAcp() {
            if (!hostedFields) {
                return Promise.reject({ message: DEFAULT_ERROR_MSG });
            }
            return hostedFields.submit();
        }
        function applyPaypalAddressToCart(paypalData) {
            return checkoutService.getAndUpdateCart((cart) => {
                const cartAddress = createCartAddress(
                    paypalData.PAYMENTREQUEST_0_SHIPTOSTREET,
                    paypalData.PAYMENTREQUEST_0_SHIPTOSTREET2,
                    paypalData.PAYMENTREQUEST_0_SHIPTOCITY,
                    paypalData.PAYMENTREQUEST_0_SHIPTOSTATE,
                    formatZipCode(paypalData.PAYMENTREQUEST_0_SHIPTOZIP, paypalData.PAYMENTREQUEST_0_SHIPTOCOUNTRYCODE),
                    paypalData.PAYMENTREQUEST_0_SHIPTOCOUNTRYCODE,
                    paypalData.FIRSTNAME,
                    paypalData.LASTNAME,
                    paypalData.PHONENUM
                );
                return {
                    ...cart,
                    shippingAddress: cartAddress,
                    billingAddress: cartAddress,
                    postalCode: formatZipCode(cartAddress.postalCode, cartAddress.country),
                    state: cartAddress.state,
                    country: cartAddress.country,
                    selectedShippingAddressId: undefined
                };
            });
        }

        function startCheckoutFlow(cartId, getGiftCards) {
            // The following string is per PayPal API / Smart Button Documentation
            const returnUrlBase = 'https://www.paypal.com/checkoutnow/error';

            return apiService.createPaypalExpressCheckout({
                cartId,
                giftCards: getGiftCards()
            }).then((data) => {
                if (data && data.id) {
                    return data.id;
                }
                return $q.reject();
            }).catch((error) => {
                return $q.reject(error);
            });
        }

        function legacyStartCheckoutFlow(cartId, getGiftCards) {
            // The following string is per PayPal API / Smart Button Documentation
            const returnUrlBase = 'https://www.paypal.com/checkoutnow/error';

            return apiService.legacyCreatePaypalExpressCheckout({
                returnUrl: returnUrlBase,
                cancelUrl: returnUrlBase,
                giftCards: getGiftCards(),
                cartId
            }).then((data) => {
                if (data && data.token) {
                    return data.token;
                }
                return $q.reject();
            }).catch((error) => {
                return $q.reject(error);
            });
        }

        function paypalIsAvailablePaymentMethod(storeInfo) {
            return angular.isDefined(storeInfo.paypal) && storeInfo.paypal.isEnabled === true;
        }

        function hasPaypalQueryParams(params) {
            return angular.isDefined(params.token) && angular.isDefined(params.PayerID);
        }

        return {
            init,
            loadButtonsScript,
            loadCheckoutScript,
            renderPaypalAcp,
            renderPaypalButtons,
            renderPaypalMessages,
            renderPaypalSummaryButtons,
            savePaypalData,
            startCheckoutFlow,
            setPaypalDataToCart,
            applyPaypalAddressToCart,
            paypalIsAvailablePaymentMethod,
            hasPaypalQueryParams,
            submitPayPalAcp
        };
    }).name;

function getPaymentAction(storeInfo) {
    if (storeInfo.paymentAutoCapture === true) {
        return 'capture';
    }
    if (storeInfo.paypal && storeInfo.paypal.merchantPayerId && storeInfo.paypal.primaryEmailConfirmed === true) {
        return 'authorize';
    }
    return 'capture';
}


// WEBPACK FOOTER //
// ./src/app/services/paypalSdk.service.js